import React, { useState, useRef, useEffect } from 'react';
import { Share2, Copy, Check, Link, Building2, FolderOpen } from 'lucide-react';
import toast from 'react-hot-toast';

interface ShareCalendarDropdownProps {
  date: string;
  workspaceId?: string;
  organizationId?: string;
  rangeType?: 'day' | 'week' | 'month';
  rangeStartDate?: string;
  eventFilter?: 'my_events' | 'all_events';
}

const ShareCalendarDropdown: React.FC<ShareCalendarDropdownProps> = ({
  date,
  workspaceId,
  organizationId,
  rangeType = 'day',
  rangeStartDate,
  eventFilter,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Generate URLs
  const baseUrl = window.location.origin;
  
  const getUrls = () => {
    const urls: { type: string; label: string; description: string; url: string; shortUrl: string; icon: React.ReactNode }[] = [];

    const isRange = rangeType !== 'day';
    const effectiveStartDate = isRange ? (rangeStartDate || date) : date;
    const rangeLabel = rangeType === 'week' ? '7 Day' : rangeType === 'month' ? '30 Day' : '1 Day';

    const buildPublicPath = (scope: 'all' | 'org' | 'ws', scopeId?: string) => {
      if (rangeType === 'week') {
        if (scope === 'all') return `/public/calendar/all/week/${effectiveStartDate}`;
        return `/public/calendar/${scope}/${scopeId}/week/${effectiveStartDate}`;
      }
      if (rangeType === 'month') {
        if (scope === 'all') return `/public/calendar/all/month/${effectiveStartDate}`;
        return `/public/calendar/${scope}/${scopeId}/month/${effectiveStartDate}`;
      }
      // day
      if (scope === 'all') return `/public/calendar/all/${date}`;
      return `/public/calendar/${scope}/${scopeId}/${date}`;
    };

    const buildShortPath = (scope: 'a' | 'o' | 'w', scopeId?: string) => {
      if (rangeType === 'week') {
        if (scope === 'a') return `/c/a/w/${effectiveStartDate}`;
        return `/c/${scope}/${scopeId}/w/${effectiveStartDate}`;
      }
      if (rangeType === 'month') {
        if (scope === 'a') return `/c/a/m/${effectiveStartDate}`;
        return `/c/${scope}/${scopeId}/m/${effectiveStartDate}`;
      }
      // day
      if (scope === 'a') return `/c/a/${date}`;
      return `/c/${scope}/${scopeId}/${date}`;
    };

    // All events URL
    urls.push({
      type: `all-${rangeType}`,
      label: isRange ? `All Events (${rangeLabel})` : 'All Events',
      description: isRange ? `Share ${rangeLabel.toLowerCase()} calendar - no filtering` : 'Share full calendar - no filtering',
      url: `${baseUrl}${buildPublicPath('all')}`,
      shortUrl: `${baseUrl}${buildShortPath('a')}`,
      icon: <Link className="w-4 h-4" />
    });

    // Organization URL (if orgId available)
    if (organizationId) {
      urls.push({
        type: `org-${rangeType}`,
        label: isRange ? `Organization (${rangeLabel})` : 'Organization',
        description: isRange ? `Events with your tracked bills (${rangeLabel.toLowerCase()})` : 'Events with your tracked bills',
        url: `${baseUrl}${buildPublicPath('org', organizationId)}`,
        shortUrl: `${baseUrl}${buildShortPath('o', organizationId)}`,
        icon: <Building2 className="w-4 h-4" />
      });
    }

    // Workspace URL (if workspaceId available)
    if (workspaceId) {
      urls.push({
        type: `ws-${rangeType}`,
        label: isRange ? `Workspace (${rangeLabel})` : 'Workspace',
        description: isRange ? `Events with workspace bills only (${rangeLabel.toLowerCase()})` : 'Events with workspace bills only',
        url: `${baseUrl}${buildPublicPath('ws', workspaceId)}`,
        shortUrl: `${baseUrl}${buildShortPath('w', workspaceId)}`,
        icon: <FolderOpen className="w-4 h-4" />
      });
    }

    return urls;
  };

  const handleCopyLink = async (url: string, type: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(type);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const urls = getUrls();

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 text-text-secondary dark:text-text-dark-secondary bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg hover:bg-neutral-200 dark:hover:bg-background-dark-secondary transition-colors"
        title="Share Calendar"
      >
        <Share2 className="w-4 h-4" />
        <span>Share</span>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-background-dark-secondary rounded-lg shadow-lg border border-border-light dark:border-border-dark z-50">
          <div className="p-3 border-b border-border-light dark:border-border-dark">
            <h3 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">
              Share Public Calendar
            </h3>
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
              Generate a link to share this calendar publicly
            </p>
          </div>

          <div className="p-2">
            {urls.map((item) => (
              <div
                key={item.type}
                className="p-2 rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1 text-text-tertiary dark:text-text-dark-tertiary">
                    {item.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                        {item.label}
                      </span>
                      <button
                        onClick={() => handleCopyLink(item.shortUrl, item.type)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 bg-primary-50 dark:bg-primary-900/20 rounded transition-colors"
                      >
                        {copiedLink === item.type ? (
                          <>
                            <Check className="w-3 h-3" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-0.5">
                      {item.description}
                    </p>
                    <div className="mt-1.5">
                      <code className="text-xs text-text-secondary dark:text-text-dark-secondary bg-background-tertiary dark:bg-background-dark-tertiary px-1.5 py-0.5 rounded block truncate">
                        {item.shortUrl}
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-border-light dark:border-border-dark bg-background-tertiary dark:bg-background-dark-tertiary rounded-b-lg">
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
              <span className="font-medium">Note:</span> Public links never expire and show all events. Bill and legislator details require login to view.
              {eventFilter === 'my_events' && (
                <span className="block mt-1">Your &quot;My Events&quot; filter is not applied to public share links.</span>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareCalendarDropdown;

